import { apiSlice } from "features/api/apiSlice";
import { RESULTS_PER_PAGE } from "constants";
import { createEntityAdapter } from "@reduxjs/toolkit";
import { formatDate } from "utils/helpers";

const callCenterAdapter = createEntityAdapter({});

const initialState = callCenterAdapter.getInitialState({ size: 0 });

export const callCenterApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCallCenterAgents: builder.query({
      query: (searchParams) =>
        `/call_center/agents?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {
        // console.log("Raw API Response:", response); 

        // if (!Array.isArray(response)) {
        //   console.warn("Unexpected response format:", response);
        //   return callCenterAdapter.setAll(initialState, []);
        // }

        const fetchedCallCenterAgents = response.map((callCenter) => {
          const {
            domain_id,
            user_id,
            no_answer_delay_time,
            max_no_answer,
            wrap_up_time,
            reject_delay_time,
            busy_delay_time,
            created_at,
            updated_at,
            enabled,
            user,
            domain,
            ...callCenterAgentProps
          } = callCenter;

          callCenterAgentProps["enabled"] = enabled ? "Yes" : "No";
          callCenterAgentProps["domain"] = domain?.name || "Unknown";
          callCenterAgentProps["user"] = user ? `${user.lastname} ${user.firstname}` : "Unknown User";

          return {
            ...callCenterAgentProps,
            created_at: formatDate(created_at),
          };
        });

        return callCenterAdapter.setAll(
          { ...initialState, size: fetchedCallCenterAgents.length },
          fetchedCallCenterAgents
        );
      },

      providesTags: (result) =>
        result
          ? [
            ...result.ids.map((id) => ({ type: "CallCenter", id })),
            { type: "CallCenter", id: "PARTIAL-LIST" },
          ]
          : [{ type: "CallCenter", id: "PARTIAL-LIST" }],
    }),
    getCallCenterQueues: builder.query({
      query: (searchParams) =>
        `/call_center/queues?${searchParams}&limit=${RESULTS_PER_PAGE}`,
      transformResponse: (response) => {

        const fetchedQueues = response.map((queue) => {
          const {
            created_at,
            updated_at,
            enabled,
            created_by,
            domain,
            domain_id,
            info,
            parameters,
            ...queueProps
          } = queue;

          queueProps["enabled"] = enabled ? "Yes" : "No";
          queueProps["domain"] = domain?.name || "Unknown";
          queueProps["created_by"] = created_by ? `${created_by.lastname} ${created_by.firstname}` : "Unknown User";

          // Extract file name from "info" object if available
          queueProps["moh_sound_file"] = info?.["moh-sound"]?.file?.name || "No file";

          return {
            ...queueProps,
            created_at: formatDate(created_at),
          };
        });

        return callCenterAdapter.setAll(
          { ...initialState, size: fetchedQueues.length },
          fetchedQueues
        );
      },
      providesTags: (result) =>
        result
          ? [
            ...result.ids.map((id) => ({ type: "callCenter", id })),
            { type: "callCenter", id: "PARTIAL-LIST" },
          ]
          : [{ type: "callCenter", id: "PARTIAL-LIST" }],
    }),
    getCallCenterAgent: builder.query({
      query: (id) => `/call_center/agents/${id}`,
      providesTags: (result, error, id) => [{ type: "CallCenter", id }],
    }),
    getCallCenterQueue: builder.query({
      query: (id) => `/call_center/queues/${id}`,
      providesTags: (result, error, id) => [{ type: "CallCenter", id }],
    }),
    createCallCenterAgents: builder.mutation({
      query: (data) => ({
        url: "/call_center/agents",
        method: "POST",
        body: JSON.stringify({ ...data }),
      }),
      invalidateTags: [{ type: "CallCenter", id: "PARTIAL-LIST" }],
    }),
    createCallCenterQueues: builder.mutation({
      query: (data) => ({
        url: "/call_center/queues",
        method: "POST",
        body: JSON.stringify({ ...data }),
      }),
      invalidateTags: [{ type: "CallCenter", id: "PARTIAL-LIST" }],
    }),
    updateCallCenterAgents: builder.mutation({
      query: ({ callCenterAgentId, ...data }) => ({
        url: `/call_center/agents/${callCenterAgentId}`,
        method: "PATCH",
        body: JSON.stringify({
          id: callCenterAgentId,
          ...data,
        }),
      }),
      invalidateTags: (result, error, args) => [
        { type: "CallCenter", id: args.id },
      ],
    }),
    deleteCallCenterAgents: builder.mutation({
      query: (id) => ({
        url: `/call_center/agents/${id}`,
        method: "DELETE",
      }),
      invalidateTags: (result, error, args) => [
        { type: "CallCenter", id: args.id },
        { type: "CallCenter", id: "PARTIAL-LIST" },
      ],
    })
  })
})

export const {
  useGetCallCenterAgentsQuery,
  useGetCallCenterQueuesQuery,
  useCreateCallCenterAgentsMutation,
  useUpdateCallCenterAgentsMutation,
  useDeleteCallCenterAgentsMutation,
  useGetCallCenterAgentQuery,
  useGetCallCenterQueueQuery,
  useCreateCallCenterQueuesMutation,
} = callCenterApiSlice;
